import React from 'react'
import { Checkbox } from '../../legacy-design-system/checkbox'
import { SingleSelectComboboxString } from '../../legacy-design-system/combobox'
import { css, cx } from '@emotion/css'
import { TextInput } from '../../legacy-design-system/form-text'
import { I18nConsumer } from '../i18n-provider'
import { CsCloseIcon } from '../../legacy-design-system/icons'
import { GetFormFieldPropsFn } from '../../legacy-design-system/form/form-validator'
import { ContrastFormattedMessage } from '../contrast-formatted-message'

export type ParentFieldType = 'STRING' | 'NUMERIC' | 'PERSON_OF_CONTACT'
export type ChildFieldType = 'CONTACT_NAME' | 'PHONE' | 'EMAIL'
export type POCType = 'NAME_EMAIL_PHONE' | 'NAME_EMAIL' | 'NAME'

const fieldTypeTo18N: Record<ParentFieldType, string> = {
  STRING: 'title_fieldTypeFreeform',
  NUMERIC: 'title_fieldTypeNumeric',
  PERSON_OF_CONTACT: 'title_fieldTypePointOfContact'
}

const pocTypeTo18N: Record<POCType, string> = {
  NAME_EMAIL_PHONE: 'title_customMetadata_nameEmailAndPhoneField',
  NAME_EMAIL: 'title_customMetadata_nameAndEmailField',
  NAME: 'title_name'
}

const buildGetErrorMessage =
  (errorMessage: string, duplicatedErrorMessage: string) =>
  ({ validity }) => {
    if (validity && !validity.valid) {
      if (validity.valueMissing) {
        return errorMessage
      }
    }
    return duplicatedErrorMessage
  }

export interface InputField {
  readonly id: number
  readonly type?: ParentFieldType
  readonly isTypeEditable?: boolean
  readonly isAgentLabelDuplicated: boolean
  readonly displayLabel: string
  readonly isRequired: boolean
  readonly isUnique: boolean
  readonly subfields?: InputSubfield[]
}

export interface InputSubfield {
  readonly type?: ChildFieldType
  readonly displayLabel: string
  readonly isRequired: boolean
  readonly isUnique: boolean
}

export interface FieldProps extends InputField {
  readonly onEdit: (field: InputField) => void
  readonly onRemove?: () => void
  readonly getFormFieldProps: GetFormFieldPropsFn
}

export function CustomMetadataFieldInput(props: FieldProps) {
  const { onEdit, onRemove, getFormFieldProps, ...field } = props

  const {
    id,
    displayLabel,
    type: parentFieldType,
    isUnique = false,
    isRequired = false,
    isTypeEditable = true,
    isAgentLabelDuplicated,
    subfields
  } = field

  const rootContainerCss = css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  `
  const firstColumnContainerCss = css`
    min-width: 274px;
    width: 29.5%;
    margin-right: 12px;
  `
  const secondColumnContainerCss = css`
    margin-right: 28px;
    min-width: 274px;
    width: 29.5%;
    align-self: flex-end;
  `
  const displayChildLabelCss = css``
  const fieldConfigCss = css`
    display: flex;
    padding-top: 8px;
  `
  const fieldConfigSeparatorCss = css`
    margin-left: 20px;
  `
  const subfieldDividerCss = css`
    margin-top: 8px;
  `

  return (
    <div>
      <div className={rootContainerCss}>
        <div className={firstColumnContainerCss}>
          {isTypeEditable ? (
            <ContrastFormattedMessage id="detail_fieldTypePlaceholder">
              {(placeholder) => {
                return (
                  <I18nConsumer>
                    {({ intl }) => {
                      return (
                        <SingleSelectComboboxString<ParentFieldType>
                          data-testid="field-type-combobox-input"
                          comboboxDataTestId="field-type-combobox-menu"
                          placeholder={placeholder}
                          items={[
                            {
                              items: ['STRING', 'NUMERIC', 'PERSON_OF_CONTACT']
                            }
                          ]}
                          label=""
                          itemToString={(type) =>
                            intl.formatMessage({
                              id: fieldTypeTo18N[type]
                            })
                          }
                          onChange={(value) => {
                            onEdit({ ...field, type: value })
                          }}
                          value={parentFieldType}
                        />
                      )
                    }}
                  </I18nConsumer>
                )
              }}
            </ContrastFormattedMessage>
          ) : (
            <I18nConsumer>
              {({ intl }) => {
                return (
                  <TextInput
                    disabled={true}
                    readOnly={true}
                    label=""
                    value={
                      !!parentFieldType
                        ? intl.formatMessage({
                            id: fieldTypeTo18N[parentFieldType]
                          })
                        : undefined
                    }
                  />
                )
              }}
            </I18nConsumer>
          )}
        </div>

        <div className={secondColumnContainerCss}>
          <ContrastFormattedMessage id="detail_fieldNamePlaceholder">
            {(text) => (
              <ContrastFormattedMessage id="validation_requiredField">
                {(requiredErrorMsg) => (
                  <ContrastFormattedMessage id="validation_duplicatedField">
                    {(duplicatedErrorMsg) => {
                      return (
                        <TextInput
                          id={id.toString()}
                          data-testid="field-display-label-input"
                          required={!!parentFieldType}
                          type="text"
                          disabled={!parentFieldType}
                          label=""
                          renderLabel={() => undefined}
                          placeholder={text}
                          onChange={(value) => {
                            onEdit({
                              ...field,
                              displayLabel: value,
                              subfields:
                                !!subfields && !!subfields.length
                                  ? subfields.map((subfield) => {
                                      return {
                                        ...subfield,
                                        displayLabel: !!subfield.type
                                          ? buildPOCSubfieldLabel(
                                              subfield.type,
                                              value
                                            )
                                          : ''
                                      }
                                    })
                                  : undefined
                            })
                          }}
                          value={displayLabel}
                          {...getFormFieldProps({
                            id: id.toString(),
                            getErrorMessage: buildGetErrorMessage(
                              requiredErrorMsg,
                              duplicatedErrorMsg
                            ),
                            validator: ({ validity }) => {
                              return isAgentLabelDuplicated
                                ? false
                                : validity.valid
                            }
                          })}
                        />
                      )
                    }}
                  </ContrastFormattedMessage>
                )}
              </ContrastFormattedMessage>
            )}
          </ContrastFormattedMessage>
        </div>

        <div className={fieldConfigCss}>
          <span>
            <ContrastFormattedMessage id="detail_requiredField">
              {(text) => (
                <Checkbox
                  label={text}
                  value={isRequired}
                  onChange={(value) => {
                    onEdit({
                      ...field,
                      isRequired: value,
                      subfields:
                        !!subfields && subfields.length
                          ? subfields.map((subfield) => {
                              return {
                                ...subfield,
                                isRequired: value
                              }
                            })
                          : undefined
                    })
                  }}
                />
              )}
            </ContrastFormattedMessage>
          </span>
          <span className={fieldConfigSeparatorCss}>
            <ContrastFormattedMessage id="detail_uniqueFieldValue">
              {(text) => (
                <Checkbox
                  label={text}
                  value={isUnique}
                  onChange={(value) => {
                    onEdit({
                      ...field,
                      isUnique: value,
                      subfields:
                        !!subfields && subfields.length
                          ? subfields.map((subfield) => {
                              return {
                                ...subfield,
                                isUnique: value
                              }
                            })
                          : undefined
                    })
                  }}
                />
              )}
            </ContrastFormattedMessage>
          </span>
        </div>

        {!!onRemove && (
          <CsCloseIcon
            data-testid="delete-field-icon"
            size={12}
            className={css`
              align-self: center;
              margin-left: 55px;
            `}
            onMouseDown={onRemove}
          />
        )}
      </div>
      {parentFieldType === 'PERSON_OF_CONTACT' && (
        <div className={cx(subfieldDividerCss, rootContainerCss)}>
          <div className={firstColumnContainerCss}>
            <TextInput disabled={true} readOnly={true} label="" />
          </div>
          <div className={cx(secondColumnContainerCss, displayChildLabelCss)}>
            <ContrastFormattedMessage id="detail_customMetadata_pocSelectInfo">
              {(placeholder) => {
                return (
                  <ContrastFormattedMessage id="validation_requiredField">
                    {(requiredErrorMsg) => {
                      return (
                        <I18nConsumer>
                          {({ intl }) => {
                            return (
                              <SingleSelectComboboxString<POCType>
                                id={'POC-' + id.toString()}
                                placeholder={placeholder}
                                items={[
                                  {
                                    items: [
                                      'NAME',
                                      'NAME_EMAIL',
                                      'NAME_EMAIL_PHONE'
                                    ]
                                  }
                                ]}
                                label=""
                                renderLabel={() => undefined}
                                itemToString={(type) =>
                                  intl.formatMessage({
                                    id: pocTypeTo18N[type]
                                  })
                                }
                                onChange={(value) => {
                                  onEdit({
                                    ...field,
                                    subfields: buildSubfieldsFromPOCType(
                                      value,
                                      field
                                    )
                                  })
                                }}
                                value={
                                  !!subfields
                                    ? getSelectedPOC(subfields)
                                    : undefined
                                }
                                inputProps={{
                                  ...getFormFieldProps({
                                    id: 'POC-' + id.toString(),
                                    getErrorMessage: () => {
                                      return requiredErrorMsg
                                    },
                                    validator: ({ validity }) => {
                                      return !!subfields &&
                                        !!getSelectedPOC(subfields)
                                        ? true
                                        : validity.valid
                                    }
                                  }),
                                  required: true,
                                  // @ts-ignore todo TS-3.8.3 - TS-8508
                                  label: '',
                                  renderLabel: () => undefined
                                }}
                              />
                            )
                          }}
                        </I18nConsumer>
                      )
                    }}
                  </ContrastFormattedMessage>
                )
              }}
            </ContrastFormattedMessage>
          </div>
        </div>
      )}
    </div>
  )
}

export const buildPOCSubfieldLabel = (
  childType: ChildFieldType,
  parentDisplayLabel: string
): string => {
  switch (childType) {
    case 'CONTACT_NAME':
      return parentDisplayLabel + ' Name'
    case 'EMAIL':
      return parentDisplayLabel + ' Email'
    case 'PHONE':
      return parentDisplayLabel + ' Phone'
  }
}

export const buildSubfieldsFromPOCType = (
  pocType: POCType,
  field: InputField
): InputSubfield[] => {
  const { displayLabel, isRequired, isUnique } = field

  switch (pocType) {
    case 'NAME':
      return [
        {
          type: 'CONTACT_NAME',
          displayLabel: buildPOCSubfieldLabel('CONTACT_NAME', displayLabel),
          isRequired,
          isUnique
        }
      ]
    case 'NAME_EMAIL':
      return [
        {
          type: 'CONTACT_NAME',
          displayLabel: buildPOCSubfieldLabel('CONTACT_NAME', displayLabel),
          isRequired,
          isUnique
        },
        {
          type: 'EMAIL',
          displayLabel: buildPOCSubfieldLabel('EMAIL', displayLabel),
          isRequired,
          isUnique
        }
      ]
    case 'NAME_EMAIL_PHONE':
      return [
        {
          type: 'CONTACT_NAME',
          displayLabel: buildPOCSubfieldLabel('CONTACT_NAME', displayLabel),
          isRequired,
          isUnique
        },

        {
          type: 'EMAIL',
          displayLabel: buildPOCSubfieldLabel('EMAIL', displayLabel),
          isRequired,
          isUnique
        },
        {
          type: 'PHONE',
          displayLabel: buildPOCSubfieldLabel('PHONE', displayLabel),
          isRequired,
          isUnique
        }
      ]
    default:
      return []
  }
}

export function getSelectedPOC(subfields: InputSubfield[]) {
  let selectedPOC: POCType

  if (subfields.length) {
    const hasName = subfields.find(
      (subfield) => subfield.type === 'CONTACT_NAME'
    )
    const hasEmail = subfields.find((subfield) => subfield.type === 'EMAIL')
    const hasPhone = subfields.find((subfield) => subfield.type === 'PHONE')

    if (hasName) {
      selectedPOC = 'NAME'
      if (hasEmail) {
        selectedPOC = 'NAME_EMAIL'
        if (hasPhone) {
          selectedPOC = 'NAME_EMAIL_PHONE'
        }
      }
      return selectedPOC
    }
  }

  return undefined
}
